import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useApp } from '../../lib/app/AppContext';

export function LoginPage() {
  const { user, accountEmail, hasAccount, register, login } = useApp();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState(accountEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      if (!hasAccount) {
        if (name.trim().length < 2 || !email.trim() || password.length < 8) {
          setError('Enter a name, valid email, and password with at least 8 characters.');
          return;
        }
        await register(name, email, password);
        void navigate('/dashboard');
        return;
      }

      const ok = await login(email, password);
      if (!ok) {
        setError('Email or password is incorrect.');
        return;
      }
      void navigate('/dashboard');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-hero">
        <Link className="brand-mark large" to="/" aria-label="Return to GPT Build home">G</Link>
        <span className="page-kicker">Standalone SaaS application</span>
        <h1>Operate everything from one intelligent workspace.</h1>
        <p>A complete local-first application with protected routes, persistent state, CRUD workflows, settings, inbox, analytics, and responsive navigation.</p>
        <div className="login-points">
          <span>✓ Local-first persistence</span>
          <span>✓ No external backend required</span>
          <span>✓ Hashed local credentials</span>
        </div>
      </section>

      <section className="login-card panel">
        <div>
          <span className="page-kicker">{hasAccount ? 'Welcome back' : 'First-run setup'}</span>
          <h2>{hasAccount ? 'Sign in' : 'Create your local admin'}</h2>
          <p>{hasAccount ? 'Use the account created in this browser.' : 'This account is stored only in this browser.'}</p>
        </div>

        <form onSubmit={(event) => void submit(event)} className="form-stack">
          {!hasAccount ? (
            <label>Name<input value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" placeholder="Workspace admin" required /></label>
          ) : null}
          <label>Email<input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="username" placeholder="you@company.com" required /></label>
          <label>Password<input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete={hasAccount ? 'current-password' : 'new-password'} minLength={8} required /></label>
          {error ? <div className="form-error">{error}</div> : null}
          <button className="btn primary full" type="submit" disabled={submitting}>
            {submitting ? 'Working…' : hasAccount ? 'Sign in' : 'Create account'}
          </button>
        </form>

        <Link className="login-back-link" to="/">← Back to template overview</Link>
      </section>
    </main>
  );
}
